import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Mail, Lock, Info, Eye, EyeOff } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  usePageMeta({
    title: "Log in — ColourMatch",
    description: "Log in to ColourMatch to access your saved projects, renders and paint specifications.",
    path: "/login",
  });

  // Session-expired banner is shown when the global 401 interceptor bounced
  // the user back here (see /lib/api.js). `from` carries the path they were
  // on so we return them to it after a successful sign-in.
  const sessionExpired = searchParams.get("reason") === "session-expired";
  const fromParam = searchParams.get("from");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      // Priority: ?from= query param → router state → sensible default.
      const next = fromParam || loc.state?.from || (u.role === "admin" ? "/admin" : "/projects");
      nav(next);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-10" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <span className="w-10 h-10 bg-brand-navy rounded-2xl flex items-center justify-center">
              <span className="block w-4 h-4 bg-white rounded-md" />
            </span>
          </Link>
          <h1 className="text-4xl font-bold tracking-tight text-brand-ink mb-2">Welcome back</h1>
          <p className="text-brand-muted">Sign in to continue your projects.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          {sessionExpired && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-4 py-3 text-sm flex items-start gap-2" data-testid="login-session-expired-banner">
              <Info size={16} className="shrink-0 mt-0.5" />
              <span>Your session expired. Please sign in to continue.</span>
            </div>
          )}
          {err && (
            <div className="bg-destructive/5 border border-destructive/20 text-destructive rounded-2xl px-4 py-3 text-sm" data-testid="login-error">
              {err}
            </div>
          )}

          <div className="relative">
            <Mail size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              data-testid="login-email-input"
              className="w-full h-14 pl-12 pr-5 bg-white border border-brand-border rounded-2xl text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
            />
          </div>

          <div className="relative">
            <Lock size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="login-password-input"
              className="w-full h-14 pl-12 pr-14 bg-white border border-brand-border rounded-2xl text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="login-password-toggle"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full inline-flex items-center justify-center text-brand-muted hover:text-brand-ink hover:bg-brand-linen transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="text-right -mt-2">
            <Link to="/forgot-password" data-testid="login-forgot-link"
              className="text-sm text-brand-muted hover:text-brand-teal">
              Forgot your password?
            </Link>
          </div>

          <button
            type="submit" disabled={busy}
            data-testid="login-submit-btn"
            className="w-full h-14 bg-brand-navy text-white rounded-2xl text-base font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy ? "Signing in…" : <>Continue <ArrowRight size={18} /></>}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-brand-muted">
          New here?{" "}
          <Link to="/register" className="text-brand-ink font-semibold hover:text-brand-teal" data-testid="login-to-register-link">
            Create an account
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
